import { get } from 'lodash';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { IResponse as data, IRelatedMember } from 'ui/interfaces/entities/personalInformation';

import actions from './action';
import { IMetaData } from 'patientCommons/interfaces/entities/patient/detail';
import { IError } from 'ui/interfaces/entities/apiError';

export interface IReducerState extends data {
  personalInformationErrorIndices: {};
  personalInformationSaveStatus: 'SUCCESS' | 'FAILED' | null | number;
  metaLoading?: boolean;
  metaError?: string | IError;
  personalInformationLoading: boolean;
  personalInformationError: string;
  isPersonalInformationLoaded: boolean;
  currentMode: {
    action: 'view' | 'create' | 'edit' | 'delete';
    name: string;
    displayName: string;
    // Track member data for care custodian edit
    memberData?: IRelatedMember;
  };
  metaData: IMetaData;
}

const INITIAL_STATE: IReducerState = {
  personal: {
    additional_details: {
      ethnicity: {
        code: '',
        name: '',
      },
      marital_status: {
        code: '',
        name: '',
      },
    },
    personal_details: {
      first_name: '',
      last_name: '',
      middle_name: '',
      dob: '',
      gender: '',
    },
  },
  contact: {
    related_members: [],
    contact: {
      phone_number: [],
      email: [],
      address: [],
      homeless: {},
      consent: {},
    },
    insurance: [],
    preferences: {
      functional_limitations: [],
      known_languages: [],
      preferred_method_of_contact: {},
      preferred_time: [],
      enrolled_program: {},
    },
  },
  schema: {
    profile: [],
  },
  personalInformationLoading: false,
  personalInformationError: '',
  isPersonalInformationLoaded: false,
  currentMode: {
    action: 'view',
    name: '',
    displayName: '',
  },
  metaData: {
    address_types: [],
    functional_limitations: [],
    languages: [],
    modes_of_contact: [],
    phone_number_types: [],
    preferred_time_days: [],
    states: [],
    caregiver_relations: [],
    family_member_relations: [],
    related_members_relations: [],
    relation_types: [],
  },
  personalInformationErrorIndices: {},
  personalInformationSaveStatus: null,
  metaLoading: false,
  metaError: '',
};

const reducer = reducerWithInitialState<IReducerState>(INITIAL_STATE)
  .case(actions.getPersonalInformation.started, state => ({
    ...state,
    personalInformationLoading: true,
    personalInformationError: '',
  }))
  .case(actions.getPersonalInformation.done, (state, payload) => {
    return {
      ...state,
      ...payload.result,
      personalInformationLoading: false,
      personalInformationError: '',
      isPersonalInformationLoaded: true,
    };
  })
  .case(actions.getPersonalInformation.failed, (state, payload) => ({
    ...state,
    personalInformationError: get(payload.error, 'error.message', 'Something Went Wrong!'),
    personalInformationLoading: false,
  }))
  .case(actions.setCurrentMode, (state, payload) => ({
    ...state,
    currentMode: {
      ...state.currentMode,
      action: payload.action,
      name: payload.name,
      displayName: payload.displayName,
      memberData: payload.memberData,
    },
  }))
  .case(actions.getMetaData.started, state => ({
    ...state,
    metaLoading: true,
  }))
  .case(actions.getMetaData.done, (state, payload) => ({
    ...state,
    metaLoading: false,
    metaError: '',
    metaData: payload.result,
  }))
  .case(actions.getMetaData.failed, (state, payload) => ({
    ...state,
    metaLoading: false,
    metaError: get(payload, 'error.error.message', 'Something Went Wrong!'),
  }))
  .case(actions.putPersonalInformation.started, state => ({
    ...state,
    personalInformationLoading: true,
    personalInformationSaveStatus: null,
    personalInformationErrorIndices: {},
  }))
  .case(actions.putPersonalInformation.done, (state, payload) => {
    const duplicate = get(payload, 'result.error', '');
    return {
      ...state,
      personalInformationLoading: false,
      personalInformationSaveStatus: duplicate.code === 207 ? duplicate.code : 'SUCCESS',
    };
  })
  .case(actions.putPersonalInformation.failed, (state, payload) => {
    return {
      ...state,
      personalInformationLoading: false,
      personalInformationSaveStatus: 'FAILED',
      personalInformationErrorIndices: get(payload, 'error.response.data.error_indices', {}),
    };
  })
  .build();

export default reducer;
